import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Typography} from '@mui/material'
import moment from 'moment'
import ChartCard from 'src/components-new/data/ChartCard'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import SurveyDialogNew from 'src/pages-new/patient/tabs/surveys/SurveyDialogNew'

interface SurveysTabProps {
  patientId: string
}

interface SurveyRow {
  _id: string
  name?: string
  state?: string
  translatedState?: string
  [key: string]: any
}

interface SurveyColumnConfig {
  id: string
  label: string
  type?: string
  format?: string
}

interface SurveyGraphData {
  moriskyScores: any[]
  compilationDiaryScores: any[]
  satisfactionScores: any[]
}

const STATE_LABELS: Record<string, string> = {
  CONFIRMED: 'Confermato',
  COMPILED: 'Compilato',
  TO_COMPILE: 'Da compilare',
}

function getSurveyStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (state === 'CONFIRMED') return 'success'
  if (state === 'COMPILED') return 'info'
  if (state === 'TO_COMPILE') return 'warning'
  return 'default'
}

const DEFAULT_COLUMNS: SurveyColumnConfig[] = [
  {id: 'name', label: 'NOME'},
  {id: 'translatedState', label: 'STATO'},
  {id: 'createdAt', label: 'DATA CREAZIONE', type: 'date', format: 'ddd DD MMM YYYY'},
  {id: 'requestDate', label: 'DATA COMPILAZIONE', type: 'date', format: 'ddd DD MMM YYYY'},
]

const SURVEY_STATE_OPTIONS = Object.entries(STATE_LABELS).map(([value, label]) => ({value, label}))

// PSP-driven columnConfig only ever carries {id, label} — never `type`/`format` — so date-ness
// is inferred from the known field ids instead of trusting col.type, which the PSP config
// never sets (see the identical fix in questionnairesColumns.tsx).
const DATE_COLUMN_IDS = new Set(['createdAt', 'requestDate'])

// Each filter mirrors a column (columnId) but doesn't share its id, since a filter can target
// a differently-shaped value (e.g. requestDate -> a rangePicker pair). A filter is only shown
// when its mirrored column is present in columnConfig, so disabling a column via PSP config
// also hides the filter that would otherwise let users query a field they can't see.
const ALL_FILTERS: Array<FilterItem & {columnId: string}> = [
  {id: 'name', columnId: 'name', label: 'Nome', type: 'string'},
  {id: 'state', columnId: 'translatedState', label: 'Stato', type: 'select', options: SURVEY_STATE_OPTIONS},
  {id: 'requestDate', columnId: 'requestDate', label: 'Data di compilazione', type: 'rangePicker'},
]

function buildColumns(columnConfig: SurveyColumnConfig[]): ColumnDef<SurveyRow>[] {
  return columnConfig.map(col => {
    if (col.id === 'translatedState' || col.id === 'state') {
      return {
        key: col.id,
        header: col.label,
        mobileCardBadge: true,
        render: (row: SurveyRow) => (
          <StatusPill label={STATE_LABELS[row.state ?? ''] ?? row.state ?? '—'} color={getSurveyStateColor(row.state)} />
        ),
      }
    }

    return {
      key: col.id,
      header: col.label,
      render: (row: SurveyRow) => {
        if (DATE_COLUMN_IDS.has(col.id) && row[col.id]) {
          return <Typography variant="body2">{moment(row[col.id]).format(col.format ?? 'ddd DD MMM YYYY')}</Typography>
        }
        return <Typography variant="body2">{row[col.id] ?? '—'}</Typography>
      },
    }
  })
}

function SurveyChart({title, data}: {title: string; data: any[]}): JSX.Element {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Card title={title}>
        <EmptyState title="Nessun dato disponibile" description="Non ci sono statistiche per questo grafico." />
      </Card>
    )
  }

  const xAxisLabels = [3, 2, 1, 0].map(n => moment().subtract(n, 'months').format('MMM YYYY'))

  const options: Highcharts.Options = {
    chart: {type: 'column'},
    title: {text: undefined},
    legend: {enabled: false},
    xAxis: {categories: xAxisLabels},
    yAxis: {min: 0, title: {text: undefined}},
    colors: ['#4472C4'],
    plotOptions: {
      series: {animation: false},
      column: {
        dataLabels: {
          enabled: true,
          style: {color: 'black', textOutline: 'none', fontSize: '12px', fontWeight: '100'},
        },
      },
    },
    credits: {enabled: false},
    series: [{type: 'column', data}],
  }

  return <ChartCard title={title} options={options} />
}

export default function SurveysTab({patientId}: SurveysTabProps): JSX.Element {
  const graphs: boolean = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.surveysConfiguration?.graphs
  )
  const patientSurveysColumnOptions: SurveyColumnConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.patientSurveysConfiguration?.columnOptions
  )
  const columnConfig = patientSurveysColumnOptions?.length > 0 ? patientSurveysColumnOptions : DEFAULT_COLUMNS

  const [graphData, setGraphData] = useState<SurveyGraphData>({
    moriskyScores: [],
    compilationDiaryScores: [],
    satisfactionScores: [],
  })
  const [graphsStatus, setGraphsStatus] = useState<string>(STATUS.PENDING)

  const [rows, setRows] = useState<SurveyRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: ''})
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])

  const [selectedSurvey, setSelectedSurvey] = useState<SurveyRow>()
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (!graphs) return
    apiClient.getPatientSurveyGraphData(patientId, setGraphsStatus)
      .then((data: any) => { if (data) setGraphData(data) })
      .catch(() => setGraphsStatus(STATUS.REJECTED))
  }, [patientId, graphs])

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getPatientsSurveys(patientId, activeFilters, setStatus, pagination),
      apiClient.getPatientsSurveysCount(patientId, activeFilters, setStatusCount),
    ])
      .then(([surveys, countResult]) => {
        if (Array.isArray(surveys)) {
          setRows(surveys.map((survey: any) => ({
            ...survey,
            translatedState: STATE_LABELS[survey.state] ?? survey.state,
          })))
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleRowClick = (row: SurveyRow) => {
    if (row.state === 'COMPILED' || row.state === 'CONFIRMED') {
      setSelectedSurvey(row)
      setDialogOpen(true)
    }
  }

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const columns = buildColumns(columnConfig)

  // Only offer a filter if its column is actually shown — e.g. if a PSP config disables the
  // "STATO" column, the "Stato" filter disappears from the drawer too, rather than letting
  // someone filter on data the table never displays.
  const filterConfig: FilterItem[] = useMemo(() => {
    const visibleColumnIds = new Set(columnConfig.map(col => col.id))
    return ALL_FILTERS
      .filter(({columnId}) => visibleColumnIds.has(columnId))
      .map(({columnId, ...filterItem}) => filterItem)
  }, [columnConfig])

  function renderGraphs(): JSX.Element {
    if (graphsStatus === STATUS.PENDING) {
      return (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress />
        </Box>
      )
    }

    if (graphsStatus === STATUS.REJECTED) {
      return (
        <InlineMessage severity="error" sx={{mb: 3}}>
          {'Impossibile caricare i grafici dei questionari.'}
        </InlineMessage>
      )
    }

    return (
      <Box sx={{display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3}}>
        <SurveyChart title="Questionario Morisky" data={graphData.moriskyScores} />
        <SurveyChart title="Questionario di rilevazione soddisfazione per il programma" data={graphData.satisfactionScores} />
        <SurveyChart title="Questionario monitoraggio diario" data={graphData.compilationDiaryScores} />
      </Box>
    )
  }

  return (
    <Box>
      {graphs && renderGraphs()}

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i questionari.'}
        </InlineMessage>
      )}

      {filterConfig.length > 0 && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
          <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
        </Box>
      )}

      <DataTable<SurveyRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun questionario trovato"
        onRowClick={handleRowClick}
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      <SurveyDialogNew
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        survey={selectedSurvey}
      />
    </Box>
  )
}
